import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenantId } from './useTenantId';

export interface PerformanceSummary {
  id: string;
  name: string;
  company_type: string;
  prequalification_status: string;
  overall_rating: number;
  safety_rating: number;
  quality_rating: number;
  reliability_rating: number;
  tenant_id: string;
  total_ratings: number;
  total_documents: number;
  verified_documents: number;
  pending_documents: number;
  rejected_documents: number;
  last_rating_date: string;
  last_document_upload: string;
}

export interface DocumentMetrics {
  document_type: string;
  total_count: number;
  verified_count: number;
  pending_count: number;
  rejected_count: number;
  avg_verification_hours: number;
  tenant_id: string;
}

export interface RatingTrend {
  month: string;
  company_id: string;
  avg_safety: number;
  avg_quality: number;
  avg_reliability: number;
  avg_overall: number;
  rating_count: number;
  tenant_id: string;
}

export function usePartnerAnalytics() {
  const { tenantId } = useTenantId();

  const { data: performanceSummary, isLoading: loadingPerformance } = useQuery({
    queryKey: ['partner-analytics-performance', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('contractor_performance_summary')
        .select('*')
        .eq('tenant_id', tenantId);

      if (error) throw error;
      return data as PerformanceSummary[];
    },
    enabled: !!tenantId,
  });

  const { data: documentMetrics, isLoading: loadingDocuments } = useQuery({
    queryKey: ['partner-analytics-documents', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('document_verification_metrics')
        .select('*')
        .eq('tenant_id', tenantId);

      if (error) throw error;
      return data as DocumentMetrics[];
    },
    enabled: !!tenantId,
  });

  const { data: ratingTrends, isLoading: loadingTrends } = useQuery({
    queryKey: ['partner-analytics-trends', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('rating_trends_monthly')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('month', { ascending: true });

      if (error) throw error;
      return data as RatingTrend[];
    },
    enabled: !!tenantId,
  });

  // Calculate aggregate metrics - ONLY include contractors with ratings
  const contractorsWithRatings = performanceSummary?.filter(p => p.overall_rating !== null && p.overall_rating > 0) || [];
  const contractorsWithSafetyRatings = performanceSummary?.filter(p => p.safety_rating !== null && p.safety_rating > 0) || [];
  const contractorsWithQualityRatings = performanceSummary?.filter(p => p.quality_rating !== null && p.quality_rating > 0) || [];
  const contractorsWithReliabilityRatings = performanceSummary?.filter(p => p.reliability_rating !== null && p.reliability_rating > 0) || [];

  const aggregateMetrics = {
    totalContractors: performanceSummary?.length || 0,
    averageRating: contractorsWithRatings.length > 0
      ? contractorsWithRatings.reduce((sum, p) => sum + p.overall_rating, 0) / contractorsWithRatings.length
      : 0,
    pendingDocuments: performanceSummary?.reduce((sum, p) => sum + p.pending_documents, 0) || 0,
    expiringSoon: performanceSummary?.filter(p => p.prequalification_status === 'expiring_soon').length || 0,
    averageSafetyRating: contractorsWithSafetyRatings.length > 0
      ? contractorsWithSafetyRatings.reduce((sum, p) => sum + p.safety_rating, 0) / contractorsWithSafetyRatings.length
      : 0,
    averageQualityRating: contractorsWithQualityRatings.length > 0
      ? contractorsWithQualityRatings.reduce((sum, p) => sum + p.quality_rating, 0) / contractorsWithQualityRatings.length
      : 0,
    averageReliabilityRating: contractorsWithReliabilityRatings.length > 0
      ? contractorsWithReliabilityRatings.reduce((sum, p) => sum + p.reliability_rating, 0) / contractorsWithReliabilityRatings.length
      : 0,
  };

  const topPerformers = performanceSummary
    ?.filter(p => p.overall_rating)
    ?.sort((a, b) => (b.overall_rating || 0) - (a.overall_rating || 0))
    .slice(0, 10);

  const atRiskContractors = performanceSummary?.filter(
    p => (p.overall_rating || 0) < 3 || p.pending_documents > 2 || p.rejected_documents > 0
  );

  return {
    performanceSummary,
    documentMetrics,
    ratingTrends,
    aggregateMetrics,
    topPerformers,
    atRiskContractors,
    isLoading: loadingPerformance || loadingDocuments || loadingTrends,
  };
}
